/**
 * Site-metadata helper functions used by `useFetchConfig`.
 *
 * These helpers each have **two implementations**, switched by the
 * `rovo_fps_use_rovo_config` feature gate:
 *
 * - **On** — delegate to the canonical implementation in
 *   `@atlassian/rovo-connectors-config/utils/site-metadata-helpers`. This is
 *   the path that will become the sole implementation once the gate is
 *   fully ramped and `useFetchConfig` is replaced by `useRovoConfig`.
 * - **Off** — run the original inline logic. Preserves verbatim behaviour
 *   for everyone not on the gate.
 *
 * Both branches must return identical results for any given input — see
 * `__tests__/site-metadata-helpers-equivalence.test.ts` for the `ffTest.both`
 * coverage that enforces this.
 *
 * **Not exported from the package.** Lives at
 * `controllers/config/site-metadata-helpers.ts` and is intentionally absent
 * from `package.json` `"exports"`. External consumers should keep going
 * through the `./use-fetch-config` subpath.
 */

import { fg } from '@atlaskit/platform-feature-flags/fg';
import {
	getFirstCloudId as getFirstCloudIdFromRovoConfig,
	getJiraIssueIndexedForTenant as getJiraIssueIndexedForTenantFromRovoConfig,
	getProductDataRegions as getProductDataRegionsFromRovoConfig,
	getProductEdition as getProductEditionFromRovoConfig,
	getRovoFeatures as getRovoFeaturesFromRovoConfig,
	isRovoEnabledForSite,
} from '@atlassian/rovo-connectors-config/utils/site-metadata-helpers';
import { type TSearchConfigAPIResponse } from '@atlassian/search-client/fetch-search-config';

/** Matches `ari:cloud:<product>::<resource-type>/<cloudId>` and captures the cloudId. */
const EXTRACT_CLOUD_ID = /ari:cloud:.*?\/(.*)/;

export const getFirstCloudId = (firstPartyResponse: TSearchConfigAPIResponse): string => {
	if (fg('rovo_fps_use_rovo_config')) {
		return getFirstCloudIdFromRovoConfig(firstPartyResponse);
	}
	const match = firstPartyResponse.resources[0]?.id.match(EXTRACT_CLOUD_ID);
	const extractedCloudId = match ? match[1] : '';

	return extractedCloudId;
};

export const isRovoEnabled = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
): boolean | undefined => {
	if (fg('rovo_fps_use_rovo_config')) {
		return isRovoEnabledForSite(firstPartyResponse, cloudId);
	}
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return false;
	}
	return firstPartyResponse.siteMetadata.find((metadata) => metadata.siteId === cloudId)
		?.isRovoEnabled;
};

export const getJiraIssueIndexedForTenant = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
): boolean | undefined => {
	if (fg('rovo_fps_use_rovo_config')) {
		return getJiraIssueIndexedForTenantFromRovoConfig(firstPartyResponse, cloudId);
	}
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return false;
	}
	return firstPartyResponse.siteMetadata.find((metadata) => metadata.siteId === cloudId)
		?.isJiraIssueIndexed;
};

export const getProductEdition = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
): TSearchConfigAPIResponse['siteMetadata'][number]['productEdition'] | undefined => {
	if (fg('rovo_fps_use_rovo_config')) {
		return getProductEditionFromRovoConfig(firstPartyResponse, cloudId);
	}
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return undefined;
	}

	return firstPartyResponse.siteMetadata.find(
		(metadata) => metadata.siteId === cloudId && !!metadata.productEdition,
	)?.productEdition;
};

export const getProductDataRegions = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
): TSearchConfigAPIResponse['siteMetadata'][number]['productDataRegions'] | undefined => {
	if (fg('rovo_fps_use_rovo_config')) {
		return getProductDataRegionsFromRovoConfig(firstPartyResponse, cloudId);
	}
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return undefined;
	}

	return firstPartyResponse.siteMetadata.find(
		(metadata) => metadata.siteId === cloudId && !!metadata.productDataRegions,
	)?.productDataRegions;
};

export const getRovoFeatures = (
	firstPartyResponse?: TSearchConfigAPIResponse,
	cloudId?: string,
): TSearchConfigAPIResponse['siteMetadata'][number]['rovoFeatures'] | undefined => {
	if (fg('rovo_fps_use_rovo_config')) {
		return getRovoFeaturesFromRovoConfig(firstPartyResponse, cloudId);
	}
	if (!cloudId || !firstPartyResponse || !firstPartyResponse.siteMetadata) {
		return undefined;
	}

	return firstPartyResponse.siteMetadata.find(
		(metadata) => metadata.siteId === cloudId && !!metadata.rovoFeatures,
	)?.rovoFeatures;
};
